import React, { useState } from 'react';
import { ArrowRight, Zap, Target, Settings, Rocket, CheckCircle, Mail, Linkedin, Calendar, Menu, X, Phone, Award, Briefcase, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const processes = [
    {
      icon: Target,
      step: '01',
      title: 'Discovery',
      description: 'Deep dive into your workflows to identify automation opportunities and bottlenecks.'
    },
    {
      icon: Settings,
      step: '02',
      title: 'Design',
      description: 'Blueprint scalable automation architecture tailored to your business needs.'
    },
    {
      icon: Zap,
      step: '03',
      title: 'Build',
      description: 'Develop robust automation using UiPath, Robocorp Robot Framework in Python and Gen AI.'
    },
    {
      icon: Rocket,
      step: '04',
      title: 'Deploy',
      description: 'Seamless integration and deployment with ongoing support and optimization.'
    }
  ];

  const painPoints = [
    {
      title: 'Eliminate 90% of manual data entry',
      description: 'Free your team from repetitive tasks and reduce human error'
    },
    {
      title: 'Reduce processing time from hours to minutes',
      description: 'Accelerate operations and improve customer satisfaction'
    },
    {
      title: 'Scale operations without scaling headcount',
      description: 'Grow your business efficiently with automated workflows'
    },
    {
      title: '24/7 automation without overtime costs',
      description: 'Bots work around the clock, ensuring continuous productivity'
    }
  ];

  const caseStudies = [
    {
      problem: 'WriteOff Creation Process',
      solution: 'Robocorp in Python',
      result: 'Reduced human effort by 80%',
      impact: '80% time savings',
      industry: 'Finance'
    },
    {
      problem: 'Invoice Data Review with GenAI',
      solution: 'UiPath with GenAI for Data Extraction',
      result: 'Reduced manual entry by 60%',
      impact: 'Saved 100+ hours monthly',
      industry: 'Construction'
    },
    {
      problem: 'SAP User Creation & Reporting',
      solution: 'UiPath SAP Integration',
      result: '60% error reduction',
      impact: '2+ FTE savings',
      industry: 'Construction'
    },
    {
      problem: 'Salesforce Automation',
      solution: 'UiPath and Robocorp with Python',
      result: '5+ projects delivered',
      impact: '50+ hours saved monthly',
      industry: 'Hospitality'
    },
    {
      problem: 'Marketo Automation',
      solution: 'UiPath and Robocorp with Python and GenAI',
      result: '3+ projects delivered',
      impact: '60+ hours saved monthly',
      industry: 'Hospitality'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Orinlo Technologies Logo" className="h-8 w-8 sm:h-10 sm:w-10" />
            <div className="text-xl sm:text-2xl font-bold text-slate-800">Orinlo Technologies</div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-8 ml-auto mr-6">
            <a href="#process" className="text-slate-600 hover:text-teal-600 transition-colors">Process</a>
            <a href="#solutions" className="text-slate-600 hover:text-teal-600 transition-colors">Solutions</a>
            <a href="#case-studies" className="text-slate-600 hover:text-teal-600 transition-colors">Case Studies</a>
          </nav>
          
          
        {/* Desktop CTA Button */}
        <Button 
          className="hidden md:flex bg-teal-600 hover:bg-teal-700 text-white"
          onClick={() => {
            const connectSection = document.getElementById('connect');
            if (connectSection) {
              connectSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          Connect
        </Button>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden ml-auto p-2 text-slate-600 hover:text-teal-600 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <a 
                href="#process" 
                className="text-slate-600 hover:text-teal-600 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Process
              </a>
              <a 
                href="#solutions" 
                className="text-slate-600 hover:text-teal-600 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Solutions
              </a>
              <a 
                href="#case-studies" 
                className="text-slate-600 hover:text-teal-600 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Case Studies
              </a>
              <Button 
                className="bg-teal-600 hover:bg-teal-700 text-white w-full mt-2"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.open(
                    'https://calendly.com/abhishek-orinlo/30min', // <-- Replace with your actual Calendly link
                    'calendlyPopup',
                    'width=600,height=700,scrollbars=yes'
                  );
                }}
              >
                Hire Me
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center space-y-4 sm:space-y-6">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-teal-50 text-teal-700 rounded-full text-xs sm:text-sm font-medium mb-2 sm:mb-4">
              RPA & Gen AI Automation Specialist
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight px-2">
              Automating the Mundane.
              <br />
              <span className="text-teal-600">Scaling the Exceptional.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-3xl mx-auto px-4">
              <strong>7 years of automation expertise.</strong> Automation specialist delivering intelligent automation solutions across Construction, Healthcare, Finance, and Hospitality industries.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-4 sm:pt-6 px-4">
              <Button 
                size="lg" 
                className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto"
                onClick={() => {
                  window.open(
                    'https://calendly.com/abhishek-orinlo/30min',
                    'calendlyPopup',
                    'width=600,height=700,scrollbars=yes'
                  );
                }}
                component="a"
                href="https://calendly.com/abhishek-orinlo/30min"
                target="_blank"
                rel="noopener noreferrer"
                title="Book a Discovery Call on Calendly"
                style={{ cursor: 'pointer' }}
              >
                Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a 
                href="https://drive.google.com/file/d/1kUuSxcNmM-NpKV8AK8PH0-ChAxSw5daq/view"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-300 hover:border-teal-600 hover:text-teal-600 w-full sm:w-auto"
                >
                  View Portfolio
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 sm:py-12 px-4 sm:px-6 bg-white border-y border-slate-200">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Briefcase className="h-8 w-8 text-teal-600" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-slate-900">7+</div>
              <div className="text-sm sm:text-base text-slate-600 mt-1">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Rocket className="h-8 w-8 text-teal-600" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-slate-900">30+</div>
              <div className="text-sm sm:text-base text-slate-600 mt-1">Projects Delivered</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="h-8 w-8 text-teal-600" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-slate-900">500+</div>
              <div className="text-sm sm:text-base text-slate-600 mt-1">Hours Saved</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Users className="h-8 w-8 text-teal-600" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-slate-900">4</div>
              <div className="text-sm sm:text-base text-slate-600 mt-1">Industries Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 sm:mb-6">About Orinlo Technologies</h2>
              <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                <p>
                  I'm <strong>Abhishek Singh</strong>, a Senior RPA & Gen AI Consultant with <strong>7+ years of experience</strong> architecting automation workflows for global enterprises. After successfully delivering <strong>30+ end-to-end automation projects</strong>, I founded <strong>Orinlo Technologies</strong> to offer personalized, high-quality automation solutions directly to businesses.
                </p>
                <p>
                  My expertise spans across <strong>Construction, Healthcare, Hospitality, and Finance</strong> industries, where I've helped organizations save hundreds of work hours and reduce operational costs through intelligent automation.
                </p>
                <p>
                  As an <strong>independent contractor</strong> operating under Orinlo Technologies, I bring enterprise-level expertise with the agility and personal attention of a dedicated consultant. No corporate overhead—just focused, results-driven automation solutions.
                </p>
              </div>
              
              {/* Certifications */}
              <div className="mt-6 sm:mt-8">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">Certifications</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a 
                    href="#" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-slate-200 hover:border-teal-400 hover:shadow-md transition-all"
                  >
                    <Award className="h-5 w-5 text-teal-600" />
                    <span className="text-xs sm:text-sm font-medium text-slate-700">UiPath Certified Professional (UiARD)</span>
                  </a>
                  <a 
                    href="#" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-slate-200 hover:border-teal-400 hover:shadow-md transition-all"
                  >
                    <Award className="h-5 w-5 text-teal-600" />
                    <span className="text-xs sm:text-sm font-medium text-slate-700">Google Cloud Engineer</span>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Professional Photo Placeholder */}
              <Card className="border-none shadow-lg bg-gradient-to-br from-slate-100 to-slate-50">
                <CardContent className="p-6 text-center">
                  <div className="w-48 h-48 mx-auto mb-4 rounded-full overflow-hidden border-4 border-teal-100 shadow-lg">
                    <img 
                      src="/abhishek.jpeg"
                      alt="Abhishek Singh - Senior RPA Consultant" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Abhishek Singh</h3>
                  <p className="text-sm text-slate-600 mt-1">RPA & Gen AI Automation Specialist</p>
                  <p className="text-xs text-slate-500 mt-2">UiPath Certified Professional</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Technical Expertise</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">Primary Platform</div>
                      <p className="text-slate-700">UiPath (Orchestrator, ReFramework, AI Center, Document Understanding)</p>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">Programming</div>
                      <p className="text-slate-700">Python, Robocorp Robot Framework, VB.NET, SQL, PowerShell</p>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">AI Integration</div>
                      <p className="text-slate-700">GenAI, Chat completion APIs, Agentic Solutions</p>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">Enterprise Apps</div>
                      <p className="text-slate-700">Salesforce, SAP, Oracle EBS, ServiceNow, JIRA</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-blue-50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Why Work With Me?</h3>
                  <ul className="space-y-2 text-sm sm:text-base text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Proven Track Record:</strong> 30+ successful projects</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Faster Turnaround:</strong> Direct communication, no bureaucracy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Cost-Effective:</strong> Enterprise expertise without agency overhead</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Personalized Service:</strong> Your success is my priority</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Process Showcase */}
      <section id="process" className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">How I Work</h2>
            <p className="text-base sm:text-lg text-slate-600 px-4">A proven 4-step approach to transform your business processes</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {processes.map((process, index) => {
              const Icon = process.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
                  <CardContent className="p-5 sm:p-6 space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                        <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600" />
                      </div>
                      <span className="text-2xl sm:text-3xl font-bold text-slate-200">{process.step}</span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">{process.title}</h3>
                    <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{process.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section id="solutions" className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">Why Automate?</h2>
            <p className="text-base sm:text-lg text-slate-600 px-4">Real problems solved with intelligent automation</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {painPoints.map((point, index) => (
              <div key={index} className="flex gap-3 sm:gap-4 p-4 sm:p-6 bg-white rounded-xl border border-slate-200 hover:border-teal-300 transition-all duration-300 hover:shadow-lg">
                <div className="flex-shrink-0 pt-1">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2">{point.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600">{point.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section id="case-studies" className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">Project Portfolio</h2>
            <p className="text-base sm:text-lg text-slate-600 px-4">Real projects delivered across industries</p>
          </div>
          <div className="relative">
            <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-teal-600 scrollbar-track-slate-200 px-4 sm:px-8">
              <div className="flex gap-4 sm:gap-6 md:justify-start">
                {caseStudies.map((study, index) => (
                  <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white w-72 sm:w-80 flex-shrink-0">
                    <CardContent className="p-5 sm:p-6 flex flex-col h-full">
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                          {study.industry}
                        </span>
                      </div>
                      <div className="space-y-2.5 sm:space-y-3 flex-grow">
                        <div>
                          <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-1">Problem</div>
                          <p className="text-sm sm:text-base text-slate-900 font-medium">{study.problem}</p>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-1">Solution</div>
                          <p className="text-sm sm:text-base text-slate-700">{study.solution}</p>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-1">Result</div>
                          <p className="text-sm sm:text-base text-slate-900 font-medium">{study.result}</p>
                        </div>
                      </div>
                      <div className="pt-3 sm:pt-4 mt-auto border-t border-slate-200">
                        <div className="text-xl sm:text-2xl font-bold text-teal-600">{study.impact}</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            <div className="text-center mt-4 sm:mt-6 text-xs sm:text-sm text-slate-500">
              ← Scroll to see more projects →
            </div>
          </div>
        </div>
      </section>

      {/* Connect Section */}
      <section id="connect" className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-none shadow-2xl bg-gradient-to-br from-teal-50 to-blue-50">
            <CardContent className="p-6 sm:p-8 md:p-12 text-center space-y-4 sm:space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900">Let's Automate Together</h2>
              <p className="text-sm sm:text-base md:text-lg text-slate-600 max-w-2xl mx-auto px-2">
                Have a repetitive process that's draining your team's time? Let's talk about how automation can transform your operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-4 sm:pt-6">
                <a
                  href="https://calendly.com/abhishek-orinlo/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Book a Discovery Call on Calendly"
                  onClick={e => {
                    e.preventDefault();
                    window.open(
                      'https://calendly.com/abhishek-orinlo/30min',
                      'calendlyPopup',
                      'width=600,height=700,scrollbars=yes'
                    );
                  }}
                  className="inline-block w-full sm:w-auto cursor-pointer"
                  style={{ textAlign: 'center' }}
                >
                  <Button
                    size="lg"
                    className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto cursor-pointer"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Book a Discovery Call
                  </Button>
                </a>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center pt-6 sm:pt-8 text-sm sm:text-base text-slate-700">
                <a href="mailto:abhishek@orinlo.com" className="flex items-center justify-center gap-2 hover:text-teal-600 transition-colors">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
                  abhishek@orinlo.com
                </a>
                <a href="tel:+917224969083" className="flex items-center justify-center gap-2 hover:text-teal-600 transition-colors">
                  <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
                  +91 (722) 496 9083
                </a>
                <a
                  href="https://www.linkedin.com/in/abhisheksingh1916/"
                  className="flex items-center justify-center gap-2 hover:text-teal-600 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin className="h-4 w-4 sm:h-5 sm:w-5" />
                  LinkedIn Profile
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 bg-slate-900 text-slate-300">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
            <div className="text-center md:text-left">
              <div className="text-xl sm:text-2xl font-bold text-white mb-2">Orinlo Technologies</div>
              <p className="text-sm sm:text-base text-slate-400">RPA & Gen AI Automation Specialist</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm sm:text-base">
              <a href="#process" className="hover:text-teal-400 transition-colors">Process</a>
              <a href="#solutions" className="hover:text-teal-400 transition-colors">Solutions</a>
              <a href="#case-studies" className="hover:text-teal-400 transition-colors">Case Studies</a>
              <a href="#connect" className="hover:text-teal-400 transition-colors">Connect</a>
            </div>
          </div>
          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-slate-800 text-center text-xs sm:text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Orinlo Technologies. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;